import {
  customElement,
  type TemplateResult,
  property,
  state,
  html,
  css,
  cssManager,
  DeesElement,
} from '@design.estate/dees-element';
import * as domtools from '@design.estate/dees-domtools';
import { zIndexRegistry } from '../../00zindex.js';
import { themeDefaultStyles } from '../../00theme.js';
import { DeesWindowLayer } from '../../00group-overlay/dees-windowlayer/dees-windowlayer.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-input-dropdown-popup': DeesInputDropdownPopup;
  }
}

@customElement('dees-input-dropdown-popup')
export class DeesInputDropdownPopup extends DeesElement {
  @property({ type: Array })
  accessor options: { option: string; key: string; payload?: any }[] = [];

  @property({ type: Boolean })
  accessor enableSearch: boolean = true;

  @property({ type: Boolean })
  accessor opensToTop: boolean = false;

  @property({ attribute: false })
  accessor triggerRect: DOMRect | null = null;

  @property({ attribute: false })
  accessor ownerComponent: HTMLElement | null = null;

  @state()
  accessor filteredOptions: { option: string; key: string; payload?: any }[] = [];

  @state()
  accessor highlightedIndex: number = 0;

  @state()
  accessor searchValue: string = '';

  @state()
  accessor menuZIndex: number = 1000;

  @state()
  accessor visible: boolean = false;

  private windowLayer: DeesWindowLayer | null = null;
  private isDestroying: boolean = false;

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        position: fixed;
        top: 0;
        left: 0;
        width: 0;
        height: 0;
        pointer-events: none;
        font-family: var(--dees-font-family);
        color: var(--dees-color-text-secondary);
      }

      * {
        box-sizing: border-box;
      }

      .selectionBox {
        position: fixed;
        pointer-events: auto;
        will-change: transform, opacity;
        transition: all var(--dees-transition-fast) var(--dees-ease-standard);
        opacity: 0;
        transform: translateY(-8px) scale(0.98);
        background: var(--dees-material-regular-bg);
        -webkit-backdrop-filter: var(--dees-material-regular-filter);
        backdrop-filter: var(--dees-material-regular-filter);
        border: 1px solid var(--dees-color-border-default);
        box-shadow: var(--dees-shadow-lg);
        min-height: var(--dees-control-height-lg);
        max-height: 300px;
        overflow: hidden;
        border-radius: var(--dees-radius-lg);
        user-select: none;
      }

      @supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
        .selectionBox {
          background: var(--dees-material-regular-bg-opaque);
        }
      }

      .selectionBox.top {
        transform: translateY(8px) scale(0.98);
      }

      .selectionBox.show {
        pointer-events: auto;
        transform: translateY(0) scale(1);
        opacity: 1;
      }

      .options-container {
        max-height: 250px;
        overflow-y: auto;
        padding: var(--dees-spacing-xs);
      }

      .option {
        transition: all var(--dees-transition-fast) var(--dees-ease-standard);
        line-height: var(--dees-control-height-sm);
        padding: 0 var(--dees-spacing-sm);
        border-radius: var(--dees-radius-sm);
        margin: 2px 0;
        cursor: pointer;
        font-size: var(--dees-font-control-size);
        color: var(--dees-color-text-secondary);
      }

      .option.highlighted {
        background: var(--dees-color-badge-default-bg);
      }

      .option:hover {
        background: var(--dees-color-badge-default-bg);
        color: var(--dees-color-text-primary);
      }

      .no-options {
        padding: var(--dees-spacing-sm);
        text-align: center;
        font-size: var(--dees-font-control-size);
        color: var(--dees-color-text-muted);
        font-style: italic;
      }

      .search {
        padding: var(--dees-spacing-xs);
        border-bottom: 1px solid var(--dees-color-border-default);
        margin-bottom: var(--dees-spacing-xs);
      }

      .search input {
        display: block;
        width: 100%;
        height: var(--dees-control-height-sm);
        padding: 0 var(--dees-spacing-sm);
        background: transparent;
        border: 1px solid var(--dees-color-border-default);
        border-radius: var(--dees-radius-sm);
        color: inherit;
        font-size: var(--dees-font-control-size);
        font-family: inherit;
        outline: none;
        transition: border-color var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .search input::placeholder {
        color: var(--dees-color-text-muted);
      }

      .search input:focus {
        border-color: var(--dees-color-accent-primary);
      }

      .options-container::-webkit-scrollbar {
        width: 8px;
      }

      .options-container::-webkit-scrollbar-track {
        background: transparent;
      }

      .options-container::-webkit-scrollbar-thumb {
        background: var(--dees-color-border-default);
        border-radius: var(--dees-radius-sm);
      }

      .options-container::-webkit-scrollbar-thumb:hover {
        background: var(--dees-color-border-strong);
      }
    `,
  ];

  public render(): TemplateResult {
    if (!this.triggerRect) return html``;

    const posStyle = this.computePositionStyle();

    return html`
      <div
        class="selectionBox ${this.visible ? 'show' : ''} ${this.opensToTop ? 'top' : 'bottom'}"
        style="${posStyle}; z-index: ${this.menuZIndex};"
      >
        ${this.enableSearch
          ? html`
              <div class="search">
                <input
                  type="text"
                  placeholder="Search options..."
                  .value="${this.searchValue}"
                  @input="${this.handleSearch}"
                  @click="${(e: Event) => e.stopPropagation()}"
                  @keydown="${this.handleSearchKeydown}"
                />
              </div>
            `
          : null}
        <div class="options-container">
          ${this.filteredOptions.length === 0
            ? html`<div class="no-options">No options found</div>`
            : this.filteredOptions.map((option, index) => {
                const isHighlighted = this.highlightedIndex === index;
                return html`
                  <div
                    class="option ${isHighlighted ? 'highlighted' : ''}"
                    @click="${() => this.selectOption(option)}"
                    @mouseenter="${() => (this.highlightedIndex = index)}"
                  >
                    ${option.option}
                  </div>
                `;
              })}
        </div>
      </div>
    `;
  }

  private computePositionStyle(): string {
    const rect = this.triggerRect!;
    const left = rect.left;
    const width = rect.width;

    if (this.opensToTop) {
      const bottom = window.innerHeight - rect.top + 4;
      return `left: ${left}px; width: ${width}px; bottom: ${bottom}px; top: auto`;
    } else {
      const top = rect.bottom + 4;
      return `left: ${left}px; width: ${width}px; top: ${top}px`;
    }
  }

  public async show(): Promise<void> {
    this.filteredOptions = this.options;
    this.highlightedIndex = 0;
    this.searchValue = '';

    // Create window layer (transparent click-catcher, no dim/blur)
    this.windowLayer = await DeesWindowLayer.createAndShow({ dimmed: false });
    this.windowLayer.addEventListener('click', () => {
      this.dispatchEvent(new CustomEvent('close-request'));
    });

    // Set z-index above the window layer
    this.menuZIndex = zIndexRegistry.getNextZIndex();
    zIndexRegistry.register(this, this.menuZIndex);
    this.style.zIndex = this.menuZIndex.toString();

    document.body.appendChild(this);

    // Animate in on next frame
    await domtools.plugins.smartdelay.delayFor(0);
    this.visible = true;

    // Add scroll/resize listeners for repositioning
    window.addEventListener('scroll', this.handleScrollOrResize, { capture: true, passive: true });
    window.addEventListener('resize', this.handleScrollOrResize, { passive: true });
  }

  public async hide(): Promise<void> {
    // Guard against double-destruction
    if (this.isDestroying) {
      return;
    }
    this.isDestroying = true;

    // Remove scroll/resize listeners
    window.removeEventListener('scroll', this.handleScrollOrResize, { capture: true } as EventListenerOptions);
    window.removeEventListener('resize', this.handleScrollOrResize);

    zIndexRegistry.unregister(this);

    this.searchValue = '';
    this.filteredOptions = this.options;
    this.highlightedIndex = 0;

    // Don't await - let window layer cleanup happen in background for instant visual feedback
    if (this.windowLayer) {
      this.windowLayer.destroy();
      this.windowLayer = null;
    }

    // Animate out via CSS transition
    this.visible = false;
    await domtools.plugins.smartdelay.delayFor(150);

    if (this.parentElement) {
      this.parentElement.removeChild(this);
    }

    this.isDestroying = false;
  }

  public async focusSearchInput(): Promise<void> {
    await this.updateComplete;
    const input = this.shadowRoot!.querySelector('.search input') as HTMLInputElement;
    if (input) input.focus();
  }

  public updateOptions(options: { option: string; key: string; payload?: any }[]): void {
    this.options = options;
    // Re-filter with current search value
    if (this.searchValue) {
      const searchLower = this.searchValue.toLowerCase();
      this.filteredOptions = this.options.filter((opt) =>
        opt.option.toLowerCase().includes(searchLower)
      );
    } else {
      this.filteredOptions = this.options;
    }
    this.highlightedIndex = 0;
  }

  private selectOption(option: { option: string; key: string; payload?: any }): void {
    this.dispatchEvent(
      new CustomEvent('option-selected', {
        detail: option,
      })
    );
  }

  private handleSearch = (event: Event): void => {
    const searchTerm = (event.target as HTMLInputElement).value;
    this.searchValue = searchTerm;
    const searchLower = searchTerm.toLowerCase();
    this.filteredOptions = this.options.filter((option) =>
      option.option.toLowerCase().includes(searchLower)
    );
    this.highlightedIndex = 0;
  };

  private handleSearchKeydown = (event: KeyboardEvent): void => {
    const key = event.key;
    const maxIndex = this.filteredOptions.length - 1;

    if (key === 'ArrowDown') {
      event.preventDefault();
      this.highlightedIndex = this.highlightedIndex + 1 > maxIndex ? 0 : this.highlightedIndex + 1;
    } else if (key === 'ArrowUp') {
      event.preventDefault();
      this.highlightedIndex = this.highlightedIndex - 1 < 0 ? maxIndex : this.highlightedIndex - 1;
    } else if (key === 'Enter') {
      event.preventDefault();
      if (this.filteredOptions[this.highlightedIndex]) {
        this.selectOption(this.filteredOptions[this.highlightedIndex]);
      }
    } else if (key === 'Escape') {
      event.preventDefault();
      this.dispatchEvent(new CustomEvent('close-request'));
    }
  };

  private handleScrollOrResize = (): void => {
    this.dispatchEvent(new CustomEvent('reposition-request'));
  };

  async disconnectedCallback() {
    await super.disconnectedCallback();
    window.removeEventListener('scroll', this.handleScrollOrResize, { capture: true } as EventListenerOptions);
    window.removeEventListener('resize', this.handleScrollOrResize);
    zIndexRegistry.unregister(this);
  }
}
